import { useQuery } from '@apollo/client/react';
import { FC, JSX } from 'react';
import { Location, Navigate, Outlet, useLocation } from 'react-router-dom';

import { CompanyType, CurrentUserDocument, UserRole } from '../graphql/generated/graphql';
import { hasChosenCompany } from '../lib/companySession';
import { useViewer } from '../providers/ViewerProvider';
import { routes } from '../routes';
import { isReturnablePath } from './returnTo';

/**
 * The `state` handed to `/login` so it can send the user back where they were.
 *
 * Returns undefined for pages that must not be revisited after signing in — `/logout` above
 * all, because capturing it here is what let one sign-out become a login→logout loop.
 * `LoginPage` prefers this state over the `?return_to` query, so it is the first place the
 * loop had to be cut.
 */
function signInRedirectState(location: Location): { from: Location } | undefined {
  return isReturnablePath(location.pathname) ? { from: location } : undefined;
}

/**
 * Verify that the user has *no* WorkOS session at all. Used by routes meant for entirely
 * unauthenticated visitors (legacy `/onboarding/register` lived here, but the new
 * self-signup flow needs a different guard — see `RequiredNewSignupRoutes`).
 *
 * If signed in to WorkOS — whether or not an Altitude account exists — we redirect to
 * home; the per-state routing then funnels the user to the right wizard step.
 */
export const RequiredNotAuthenticatedRoutes: FC = () => {
  const viewer = useViewer();

  if (viewer.currentUser || viewer.workosSession) {
    return <Navigate to={routes.get('home', 'index')} />;
  }

  return <Outlet />;
};

/**
 * Gate routes that should only run while the user has a WorkOS session but no Altitude
 * account yet — i.e. the brand-new self-signup wizard at `/onboarding/register`. Anyone
 * with an Altitude account is past this step; anyone with no WorkOS session can't be here.
 */
export const RequiredNewSignupRoutes: FC = () => {
  const viewer = useViewer();
  const location = useLocation();

  if (!viewer.workosSession) {
    return <Navigate to={routes.get('sessions', 'new')} state={signInRedirectState(location)} />;
  }
  if (viewer.currentUser) {
    return <Navigate to={routes.get('home', 'index')} replace />;
  }
  return <Outlet />;
};

/**
 * Verify that the user is **authenticated**.
 *
 * `state` is "is this account allowed to use the app" (Active = yes, Suspended = no). It
 * flips to Active automatically when the user signs in via AuthKit, so we cannot use it to
 * decide whether the welcome wizard still needs to run. That separate signal is
 * `onboardingCompletedAt` — null until the user finishes the wizard, then a timestamp.
 *
 * Funnel users with no `onboardingCompletedAt` into the wizard, and keep already-onboarded
 * users out of it. The wizard route itself is excluded from the redirect so the user can
 * actually reach it.
 *
 * `routes.get(...)` is called inside the component rather than at module scope to avoid a
 * circular import: this file is imported by `routes.tsx`, so reading `routes` at module
 * load throws "lexical declaration before initialization".
 */
export const RequiredAuthenticatedRoutes: FC = () => {
  const viewer = useViewer();
  const location = useLocation();

  // No WorkOS session → not signed in at all; bounce to /login.
  if (!viewer.workosSession) {
    return <Navigate to={routes.get('sessions', 'new')} state={signInRedirectState(location)} />;
  }
  // WorkOS session but no Altitude account → brand-new signup; bounce to the wizard.
  if (!viewer.currentUser) {
    return <Navigate to={routes.get('onboarding', 'register')} replace />;
  }

  const completeProfilePath = routes.get('onboarding', 'complete-profile');
  const needsOnboarding = !viewer.currentUser.metadata?.onboardingCompleted;
  const isOnCompleteProfile = location.pathname === completeProfilePath;

  if (needsOnboarding && !isOnCompleteProfile) {
    return <Navigate to={completeProfilePath} replace />;
  }

  // No matching "already-onboarded → bounce home" redirect: the wizard's mutation flips
  // `onboardingCompleted` mid-flow, and now that ViewerProvider mirrors Apollo cache
  // changes in real time, that flip would race the wizard's own navigation and kick the
  // user out before the StepCompleted ("You're All Set!") screen could render. The
  // wizard's final-step button is the canonical exit; we don't need a guard for the
  // already-onboarded-user-manually-types-the-URL case.

  return <Outlet />;
};

/**
 * Verify that the user is **authenticated**
 */
export const RequiredUserAdminRoutes: FC = () => {
  const viewer = useViewer();
  const location = useLocation();

  if (!viewer.currentUser) {
    return <Navigate to={routes.get('sessions', 'new')} state={{ from: location }} />;
  }

  if (viewer.currentUser.role === UserRole.General) {
    return <Navigate to={routes.get('home', 'index')} />;
  }

  if (viewer.currentCompany?.type === CompanyType.Individual) {
    return <Navigate to={routes.get('home', 'index')} />;
  }

  return <Outlet />;
};

/**
 * Verify that the user is **authenticated**
 */
export const RequiredUserRootRoutes: FC = () => {
  const viewer = useViewer();
  const location = useLocation();

  if (!viewer.currentUser) {
    return <Navigate to={routes.get('sessions', 'new')} state={{ from: location }} />;
  }

  if (viewer.currentUser.role !== UserRole.Root) {
    return <Navigate to={routes.get('home', 'index')} />;
  }

  return <Outlet />;
};

/**
 * Verify that the user is **authenticated** and is a policy approver
 */
export const RequiredUserApproverRoutes: FC = () => {
  const viewer = useViewer();
  const location = useLocation();

  if (!viewer.currentUser) {
    return <Navigate to={routes.get('sessions', 'new')} state={{ from: location }} />;
  }

  if (!viewer.currentUser.isPolicyApprover && viewer.currentUser.role === UserRole.General) {
    return <Navigate to={routes.get('home', 'index')} />;
  }

  if (viewer.currentCompany?.type === CompanyType.Individual) {
    return <Navigate to={routes.get('home', 'index')} />;
  }

  return <Outlet />;
};

/**
 * Root's home is the management section, unless they are acting as a company, or they picked a company in
 * this tab (their own included, which leaves no act-as in force): `/` is then that company's app like for anyone
 * working there.
 */
export function landsInManagement(user: { role: UserRole; actingAs?: unknown }): boolean {
  return user.role === UserRole.Root && !user.actingAs && !hasChosenCompany();
}

/**
 * The management section moved from `/crm` to `/management`. Links written before the move (Jira issues, Slack
 * messages, bookmarks) keep working by landing on the same page under the new prefix.
 */
export const LegacyManagementRedirect: FC = () => {
  const { pathname, search, hash } = useLocation();

  return <Navigate to={`${pathname.replace(/^\/crm/, '/management')}${search}${hash}`} replace />;
};

/**
 * Management has no home page of its own: Companies is where support work starts, so `/management` (the breadcrumb
 * root, older links) lands there.
 */
export const ManagementIndexRedirect: FC = () => <Navigate to={routes.get('management_companies', 'index')} replace />;

/**
 * Pick the home page based on whether the user is authenticated.
 *
 * `/` lives in a polymorphic scope (not in `RequiredAuthenticatedRoutes`), so the
 * onboarding redirect from that guard doesn't fire here automatically. We replicate the
 * same check inline: an authenticated user who hasn't completed the welcome wizard gets
 * funneled into `/onboarding/complete-profile` regardless of which entry point they use.
 */
export const HomePage: FC<{ authElem: JSX.Element; notAuthElem: JSX.Element }> = ({ authElem, notAuthElem }) => {
  const viewer = useViewer();
  // Read straight from the cache: right after an act-as the provider's copy of the viewer is one render behind, and
  // landing on the previous company's home would send a Root who just started acting back to management.
  const { data: liveViewer } = useQuery(CurrentUserDocument, { fetchPolicy: 'cache-only' });

  if (viewer.currentUser) {
    if (!viewer.currentUser.metadata?.onboardingCompleted) {
      return <Navigate to={routes.get('onboarding', 'complete-profile')} replace />;
    }
    if (landsInManagement(liveViewer?.currentUser ?? viewer.currentUser)) {
      return <Navigate to={routes.get('management_companies', 'index')} replace />;
    }
    return authElem;
  }

  // WorkOS-signed-in but no Altitude account yet → brand-new signup.
  if (viewer.workosSession) {
    return <Navigate to={routes.get('onboarding', 'register')} replace />;
  }

  return notAuthElem;
};
